import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { SignupStatusDto, VerificationChannel } from "@delta/shared";
import { useLang } from "../i18n";
import { api, ApiFailure } from "../lib/api";
import { Icon } from "../components/Icon";
import { PublicFrame } from "./Signup";

// The applicant's own signup, found by the HTTP-only status cookie set at signup. Shows the real verification state
// of each contact: verified, waiting for a code, or blocked because no SMS or email provider is configured. Nothing
// here can mark a contact verified without the code the server sent.

export function SignupStatus() {
  const { t } = useLang();
  const [s, setS] = useState<SignupStatusDto | null>(null);
  const [missing, setMissing] = useState(false);
  const load = useCallback(() => api<SignupStatusDto>("GET", "/signup/status").then(setS, () => setMissing(true)), []);
  useEffect(() => { void load(); }, [load]);

  if (missing) return (
    <PublicFrame title={t("sstTitle")}>
      <div className="state-box"><Icon name="info" /><h3>{t("sstNoSignup")}</h3><Link className="btn btn-primary" to="/signup">{t("sstStartSignup")}</Link></div>
    </PublicFrame>
  );
  if (!s) return <PublicFrame title={t("sstTitle")}><p>{t("loading")}</p></PublicFrame>;
  const blocked = s.status === "pending_verification" && (s.verification.sms.state === "blocked" || s.verification.email.state === "blocked");
  const msg = s.status === "pending_verification" && s.reviewMode === "manual_review" ? "sstPendingManual"
    : { pending_verification: "sstPendingVerification", pending_review: "sstPendingReview", activated: "sstActivated", rejected: "sstRejected" }[s.status];
  return (
    <PublicFrame title={t("sstTitle")} lede={t("sstReceived", { b: s.businessName })} wide>
      <div className="su-status" data-testid="signup-status" data-status={s.status}>
        <div className={`dlg-alert show ${s.status === "activated" ? "good" : ""}`} role="status"><Icon name={s.status === "rejected" ? "ban" : "info"} /><span>{t(msg)}</span></div>
        {s.reviewMode === "manual_review" && s.status !== "activated" && s.status !== "rejected" && (
          <div className="su-manual" role="note" data-testid="status-manual-review"><Icon name="info" /><span><b>{t("signupManualTitle")}</b> {t("sstManualBody")}</span></div>
        )}
        {blocked && (
          <div className="su-blocked" data-testid="verification-blocked"><Icon name="lock" /><div><b>{t("sstBlockedTitle")}</b>{t("sstBlockedBody")}</div></div>
        )}
        {s.status !== "rejected" && (["sms", "email"] as const).map((ch) => <Channel key={ch} s={s} ch={ch} onChange={setS} />)}
        {s.status === "activated" && <Link className="btn btn-primary" to="/login">{t("sstGoSignIn")}</Link>}
      </div>
    </PublicFrame>
  );
}

function Channel({ s, ch, onChange }: { s: SignupStatusDto; ch: VerificationChannel; onChange(x: SignupStatusDto): void }) {
  const { t } = useLang();
  const [code, setCode] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const v = s.verification[ch];
  const label = ch === "sms" ? t("sstChannelSms") : t("sstChannelEmail");
  const to = ch === "sms" ? s.mobile : s.email;
  const pill = v.state === "verified" ? <span className="pill good">{t("sstStateVerified")}</span>
    : v.state === "blocked" ? <span className="pill bad">{t("sstStateBlocked")}</span> : <span className="pill warn">{t("sstStatePending")}</span>;
  const fail = (e: unknown) => setErr(e instanceof ApiFailure
    ? e.code === "code_invalid" ? t("sstCodeInvalid") : e.code === "code_expired" ? t("sstCodeExpired") : e.code === "too_many_codes" ? t("sstWait") : e.code === "too_many_attempts" ? t("signupTooMany") : t("serverError")
    : t("serverError"));
  async function run(fn: () => Promise<SignupStatusDto>) {
    setBusy(true); setErr(null);
    try { onChange(await fn()); setCode(""); } catch (e) { fail(e); } finally { setBusy(false); }
  }
  return (
    <div className="su-chan" data-testid={`channel-${ch}`} data-state={v.state}>
      <div className="su-chan-h"><span>{label} <span className="hint num" dir="ltr">{to}</span></span>{pill}</div>
      {v.state === "pending" && s.status === "pending_verification" && (
        <>
          <span className="hint">{v.codeSent ? t("sstCodeSent", { to }) : t("sstNoCodeYet")}</span>
          <form className="su-code" noValidate onSubmit={(e) => { e.preventDefault(); void run(() => api("POST", "/signup/verify", { channel: ch, code: code.trim() })); }}>
            <input className="control num" inputMode="numeric" autoComplete="one-time-code" maxLength={6} aria-label={t("sstCode")} placeholder="••••••" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
            <button className="btn btn-primary" type="submit" disabled={busy || code.length !== 6}>{t("sstVerify")}</button>
            <button className="btn" type="button" disabled={busy} onClick={() => void run(() => api("POST", "/signup/resend", { channel: ch }))}>{t("sstResend")}</button>
          </form>
          {err && <div className="ferr" role="alert">{err}</div>}
        </>
      )}
    </div>
  );
}
